/**
 * Level blocks (05 §5.1): every solid tile of the grid becomes a block of the diorama. Faces
 * shared with a solid neighbour are left out, so a platform is one skin, not a row of boxes.
 * The front face is where the level's pattern lives; the post pass turns its creases into
 * the 1 px highlights that make it read as pixel art.
 */

import { Vector3 } from 'three';
import { isSolid, type TileGrid } from '../../levels/grid';
import { MeshBuilder } from '../kit/MeshBuilder';
import type { Palette } from '../palette';
import { type BlockStyle, front } from './blockFaces';

export type { BlockStyle } from './blockFaces';

/** Depth of the blocks, front to back, in tiles. The playfield plane z = 0 runs through their middle. */
export const BLOCK_DEPTH = 1.5;
const FRONT = BLOCK_DEPTH / 2;
const BACK = -BLOCK_DEPTH / 2;

/** Lower-left corner of a tile in world units, with the grid centred on the origin. */
export function tileOrigin(grid: TileGrid, col: number, row: number): { x: number; y: number } {
  return { x: col - grid.width / 2, y: grid.height / 2 - row - 1 };
}

const v = (x: number, y: number, z: number): Vector3 => new Vector3(x, y, z);

/** All the blocks of a level as one vertex-coloured geometry. */
export function buildBlocks(grid: TileGrid, style: BlockStyle, p: Palette): MeshBuilder {
  const mb = new MeshBuilder();
  const top = p.color('block.3');
  const side = p.color('block.1');
  const under = p.color('block.0');
  for (let row = 0; row < grid.height; row++) {
    for (let col = 0; col < grid.width; col++) {
      if (!isSolid(grid, col, row)) continue;
      const { x, y } = tileOrigin(grid, col, row);
      front(mb, style, col, row, x, y, FRONT, p);
      if (!isSolid(grid, col, row - 1)) {
        mb.quad(v(x, y + 1, FRONT), v(x + 1, y + 1, FRONT), v(x + 1, y + 1, BACK), v(x, y + 1, BACK), top);
      }
      if (!isSolid(grid, col, row + 1)) {
        mb.quad(v(x, y, BACK), v(x + 1, y, BACK), v(x + 1, y, FRONT), v(x, y, FRONT), under);
      }
      if (!isSolid(grid, col - 1, row)) {
        mb.quad(v(x, y, BACK), v(x, y, FRONT), v(x, y + 1, FRONT), v(x, y + 1, BACK), side);
      }
      if (!isSolid(grid, col + 1, row)) {
        mb.quad(v(x + 1, y, FRONT), v(x + 1, y, BACK), v(x + 1, y + 1, BACK), v(x + 1, y + 1, FRONT), side);
      }
      // The back is never seen, but it closes the solid for the shadow map.
      mb.quad(v(x + 1, y, BACK), v(x, y, BACK), v(x, y + 1, BACK), v(x + 1, y + 1, BACK), under);
    }
  }
  return mb;
}
